/**
 * Money value object.
 *
 * The entire system stores monetary amounts as **paise** (INR × 100) in
 * `INTEGER` columns. Never use `number` with a decimal point for money —
 * SQLite (D1) has no true DECIMAL type, and JS floats will silently corrupt
 * ₹0.10 additions.
 *
 * Rules:
 *   - `Money.paise` is always an integer safe for JS `number` (D1 rows top
 *     out well below Number.MAX_SAFE_INTEGER for a personal finance app).
 *   - Arithmetic returns a new `Money`. `Money` is immutable.
 *   - Parsing accepts "1234.56", "1,234.56", "₹1,234.56" — the display
 *     layer's job is to format; the parser's job is to be forgiving.
 *   - Currency is carried but multi-currency ops throw. MVP is INR-only.
 */

export type CurrencyCode = 'INR';

export class Money {
  readonly paise: number;
  readonly currency: CurrencyCode;

  private constructor(paise: number, currency: CurrencyCode) {
    if (!Number.isInteger(paise)) {
      throw new TypeError(`Money.paise must be an integer, got ${paise}`);
    }
    this.paise = paise;
    this.currency = currency;
  }

  static fromPaise(paise: number, currency: CurrencyCode = 'INR'): Money {
    return new Money(paise, currency);
  }

  /** Construct from a rupee value. Rounds to 2 decimal places (banker's). */
  static fromRupees(rupees: number, currency: CurrencyCode = 'INR'): Money {
    if (!Number.isFinite(rupees)) {
      throw new TypeError(`Money.fromRupees: not finite (${rupees})`);
    }
    return new Money(Math.round(rupees * 100), currency);
  }

  /**
   * Parse a user-entered string. Accepts common separators; rejects garbage.
   * Empty / null / undefined → throws (callers decide the empty policy).
   */
  static parse(input: string, currency: CurrencyCode = 'INR'): Money {
    if (typeof input !== 'string') {
      throw new TypeError('Money.parse: expected string');
    }
    const cleaned = input.trim().replace(/[₹,\s]/g, '');
    if (cleaned === '' || !/^-?\d+(\.\d{1,2})?$/.test(cleaned)) {
      throw new TypeError(`Money.parse: invalid amount "${input}"`);
    }
    const [whole, frac = ''] = cleaned.split('.');
    const sign = whole!.startsWith('-') ? -1 : 1;
    const wholeAbs = whole!.replace('-', '');
    const paddedFrac = (frac + '00').slice(0, 2);
    const paise = sign * (Number(wholeAbs) * 100 + Number(paddedFrac));
    return new Money(paise, currency);
  }

  static zero(currency: CurrencyCode = 'INR'): Money {
    return new Money(0, currency);
  }

  add(other: Money): Money {
    this.assertSameCurrency(other);
    return new Money(this.paise + other.paise, this.currency);
  }

  sub(other: Money): Money {
    this.assertSameCurrency(other);
    return new Money(this.paise - other.paise, this.currency);
  }

  /** Multiply by a plain scalar. Rounds half-to-even to stay stable. */
  mul(scalar: number): Money {
    if (!Number.isFinite(scalar)) {
      throw new TypeError('Money.mul: scalar must be finite');
    }
    return new Money(bankersRound(this.paise * scalar), this.currency);
  }

  isZero(): boolean {
    return this.paise === 0;
  }
  isNegative(): boolean {
    return this.paise < 0;
  }
  isPositive(): boolean {
    return this.paise > 0;
  }
  equals(other: Money): boolean {
    return this.currency === other.currency && this.paise === other.paise;
  }
  compare(other: Money): -1 | 0 | 1 {
    this.assertSameCurrency(other);
    return this.paise === other.paise ? 0 : this.paise < other.paise ? -1 : 1;
  }

  /** For DB writes. */
  toPaise(): number {
    return this.paise;
  }

  /** For plain-number display (avoid where possible; prefer `format`). */
  toRupees(): number {
    return this.paise / 100;
  }

  /**
   * Locale-aware display. Defaults to `en-IN` with the Indian digit grouping
   * (1,23,456.78). Currency symbol shown by default.
   */
  format(opts: { locale?: string; withSymbol?: boolean } = {}): string {
    const { locale = 'en-IN', withSymbol = true } = opts;
    const formatter = new Intl.NumberFormat(locale, {
      style: withSymbol ? 'currency' : 'decimal',
      currency: this.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return formatter.format(this.paise / 100);
  }

  private assertSameCurrency(other: Money): void {
    if (other.currency !== this.currency) {
      throw new TypeError(
        `Money currency mismatch: ${this.currency} vs ${other.currency}`,
      );
    }
  }
}

/** Sum a list of Money values. Throws on empty (currency unknown). */
export function sumMoney(values: Money[]): Money {
  if (values.length === 0) {
    throw new TypeError('sumMoney: cannot sum an empty list (currency unknown)');
  }
  return values.reduce((acc, m) => acc.add(m), Money.zero(values[0]!.currency));
}

/**
 * Banker's rounding (round half to even). Chosen because repeated ordinary
 * rounding introduces upward bias over many transactions — meaningful at
 * portfolio-level totals.
 */
function bankersRound(x: number): number {
  const floor = Math.floor(x);
  const diff = x - floor;
  if (diff < 0.5) return floor;
  if (diff > 0.5) return floor + 1;
  // exactly 0.5 → round to even
  return floor % 2 === 0 ? floor : floor + 1;
}
